import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { ChainSelector } from '../ui/ChainSelector';
import { useBridge } from '../../hooks/useBridge';
import { useWallet } from '../../hooks/useWallet';
import { useSNS } from '../../hooks/useSNS';
import { CHAIN_CONFIGS } from '../../utils/constants';
// import { switchNetwork } from '../../utils/web3';

export const BridgeForm: React.FC = () => {
  const { walletInfo, changeNetwork } = useWallet();
  const [isSwapping, setIsSwapping] = useState(false);
  const { 
    fromChain,
    toChain,
    selectedToken,
    amount,
    recipient,
    balance,
    fee,
    estimatedReceiveAmount,
    bridgeFee,
    feeBreakdown,
    transactionState,
    tokenAvailable,
    handleFromChainChange,
    handleToChainChange,
    handleTokenChange,
    handleAmountChange,
    handleRecipientChange,
    handleApproveToken,
    handleBridgeToken,
    needsApproval
  } = useBridge(walletInfo.address);

  const [networkError, setNetworkError] = useState<string | null>(null);
  
  // Resolve SNS name for recipient address
  const { snsName: recipientSNS } = useSNS(recipient && ethers.utils.isAddress(recipient) ? recipient : undefined);

  // Check if wallet is connected to the correct network
  useEffect(() => {
    if (!walletInfo.isConnected || !fromChain) {
      setNetworkError(null);
      return;
    }

    const targetChainId = CHAIN_CONFIGS[fromChain]?.chainId;
    if (targetChainId && targetChainId !== walletInfo.chainId) {
      setNetworkError(`Please switch to ${CHAIN_CONFIGS[fromChain].name}`);
    } else {
      setNetworkError(null);
    }
  }, [walletInfo.isConnected, walletInfo.chainId, fromChain]);

  // Handle network switch
  const handleNetworkSwitch = async () => {
    if (!fromChain) return;
    
    const targetChainId = CHAIN_CONFIGS[fromChain]?.chainId;
    if (!targetChainId) return;
    
    try {
      await changeNetwork(targetChainId);
      setNetworkError(null);
    } catch (error) {
      console.error('Failed to switch network:', error);
    }
  };

  // Validation checks
  const amountNum = amount ? parseFloat(amount) : 0;
  const balanceNum = balance ? parseFloat(balance) : 0;
  const feeNum = bridgeFee ? parseFloat(bridgeFee) : 0;

  // Check if amount exceeds balance
  const isInsufficientBalance = amountNum > 0 && balanceNum > 0 && amountNum > balanceNum;
  
  // Check if amount is less than or equal to fee
  const isAmountTooLow = amountNum > 0 && feeNum > 0 && amountNum <= feeNum;

  // Determine if the form is valid
  const isFormValid = () => {
    return (
      walletInfo.isConnected &&
      fromChain &&
      toChain &&
      selectedToken &&
      amount &&
      parseFloat(amount) > 0 &&
      recipient &&
      ethers.utils.isAddress(recipient) &&
      !networkError &&
      !isInsufficientBalance &&
      !isAmountTooLow
    );
  };

  // Determine if the token is available on the selected chain
  const isTokenAvailable = () => tokenAvailable;

  // Get available chains for "to" selector based on "from" selection
  const getToChainOptions = () => {
    if (!fromChain) return [];
    
    if (fromChain === 'shido-mainnet') {
      // If from chain is Shido, to chain can be any other chain
      return Object.keys(CHAIN_CONFIGS).filter(key => key !== 'shido-mainnet');
    } else {
      // If from chain is not Shido, to chain can only be Shido
      return ['shido-mainnet'];
    }
  };

  return (
    <Card 
      title="Bridge SHIDO" 
      subtitle="Transfer native SHIDO between Shido and Ethereum"
      elevated
    >
      <div className="space-y-6">
        {/* Network Error Alert */}
        {networkError && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 flex items-start">
            <div className="text-red-500 mr-3 flex-shrink-0">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm text-red-400">{networkError}</p>
              <Button 
                variant="danger" 
                size="sm" 
                className="mt-2" 
                onClick={handleNetworkSwitch}
              >
                Switch Network
              </Button>
            </div>
          </div>
        )}

        {/* Chain Selection */}
        <div className="relative">
          {/* Desktop: Side by side */}
          <div className="hidden md:grid md:grid-cols-2 gap-4">
            <ChainSelector
              label="From Chain"
              value={fromChain}
              onChange={handleFromChainChange}
              disabled={transactionState.isLoading}
            />
            
            <ChainSelector
              label="To Chain"
              value={toChain}
              onChange={handleToChainChange}
              disabled={!fromChain || transactionState.isLoading}
              excludeChains={fromChain ? [fromChain] : []}
              onlyChains={getToChainOptions()}
            />
          </div>
          
          {/* Mobile: Stacked with swap button */}
          <div className="md:hidden space-y-3">
            <ChainSelector
              label="From Chain"
              value={fromChain}
              onChange={handleFromChainChange}
              disabled={transactionState.isLoading}
            />
            
            {/* Mobile Swap Button */}
            {fromChain && toChain && (
              <div className="flex justify-center -my-1">
                <button
                  type="button"
                  onClick={() => {
                    if (fromChain === 'shido-mainnet' || toChain === 'shido-mainnet') {
                      setIsSwapping(true);
                      setTimeout(() => {
                        handleFromChainChange(toChain);
                        setIsSwapping(false);
                      }, 300);
                    }
                  }}
                  className={`
                    bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 
                    text-primary-400 p-2 rounded-full shadow-lg
                    border border-primary-500/20
                    transition-all duration-300
                    ${isSwapping ? 'animate-spin' : ''}
                  `}
                  disabled={transactionState.isLoading || !(fromChain === 'shido-mainnet' || toChain === 'shido-mainnet')}
                  style={{ animationDuration: '0.3s' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 rotate-90" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
                  </svg>
                </button>
              </div>
            )}
            
            <ChainSelector
              label="To Chain"
              value={toChain}
              onChange={handleToChainChange}
              disabled={!fromChain || transactionState.isLoading}
              excludeChains={fromChain ? [fromChain] : []}
              onlyChains={getToChainOptions()}
            />
          </div>
          
          {/* Desktop Animated Chain Swap Button */}
          {fromChain && toChain && (
            <button
              type="button"
              onClick={() => {
                if (fromChain === 'shido-mainnet' || toChain === 'shido-mainnet') {
                  setIsSwapping(true);
                  setTimeout(() => {
                    handleFromChainChange(toChain);
                    setIsSwapping(false);
                  }, 300);
                }
              }}
              className={`
                absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                bg-gradient-to-br from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 
                text-primary-400 p-2.5 rounded-full shadow-lg 
                hidden md:flex items-center justify-center z-10
                border border-primary-500/20 hover:border-primary-400/40
                transition-all duration-300 hover:scale-110 hover:shadow-primary-500/20
                ${isSwapping ? 'animate-spin' : ''}
              `}
              disabled={transactionState.isLoading || !(fromChain === 'shido-mainnet' || toChain === 'shido-mainnet')}
              title="Swap chains"
              style={{ animationDuration: '0.3s' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 100-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
              </svg>
            </button>
          )}
        </div>

        {/* Token Availability Warning */}
        {!isTokenAvailable() && (
          <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-3">
            <p className="text-sm text-amber-400">
              ⚠️ Wrapped SHIDO is not available on Ethereum yet. Bridge from Shido first.
            </p>
          </div>
        )}

        {/* Validation Warnings */}
        {isInsufficientBalance && (
          <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-3 flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-medium text-amber-400">Insufficient balance</p>
              <p className="text-xs text-amber-500/80 mt-0.5">
                You're trying to bridge {amountNum.toLocaleString()} {selectedToken} but only have {balanceNum.toLocaleString()} {selectedToken}
              </p>
            </div>
          </div>
        )}

        {isAmountTooLow && !isInsufficientBalance && (
          <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-3 flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-medium text-amber-400">Amount too low</p>
              <p className="text-xs text-amber-500/80 mt-0.5">
                Amount must be greater than the bridge fee ({feeNum.toLocaleString()} {selectedToken})
              </p>
            </div>
          </div>
        )}

        {/* Amount Input with Balance */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-300">Amount</label>
            {balance && parseFloat(balance) > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-400">Balance: {parseFloat(balance).toFixed(2)} {selectedToken}</span>
                <button 
                  type="button"
                  onClick={() => {
                    // Limit balance to 2 decimal places when using Max button
                    let maxAmount = balance;
                    if (maxAmount && maxAmount.includes('.')) {
                      const parts = maxAmount.split('.');
                      if (parts[1] && parts[1].length > 6) {
                        maxAmount = parts[0] + '.' + parts[1].substring(0, 6);
                      }
                    }
                    handleAmountChange(maxAmount);
                  }}
                  className="text-xs text-primary-400 hover:text-primary-300"
                >
                  Max
                </button>
              </div>
            )}
          </div>
          <Input
            type="number"
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            placeholder="0.0"
            min="0"
            step="0.000001"
            disabled={transactionState.isLoading || !isTokenAvailable()}
            fullWidth
          />
        </div>

        {/* Estimated Receive Amount */}
        {amount && parseFloat(amount) > 0 && bridgeFee && parseFloat(bridgeFee) > 0 && (
          <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-300">Total Bridge Fee:</span>
              <span className="text-sm text-blue-200">{bridgeFee} {selectedToken}</span>
            </div>
            
            {/* Fee Breakdown */}
            {feeBreakdown && (
              <div className="mt-2 pt-2 border-t border-blue-800/50 space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-400">• Transport Fee:</span>
                  <span className="text-xs text-blue-300">{feeBreakdown.transportFee} {selectedToken}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-400">• Service Fee ({feeBreakdown.serviceFeePercentage}%):</span>
                  <span className="text-xs text-blue-300">{feeBreakdown.serviceFee} {selectedToken}</span>
                </div>
              </div>
            )}
            
            <div className="flex justify-between items-center mt-2 pt-2 border-t border-blue-800/50">
              <span className="text-sm font-medium text-blue-300">You will receive (estimate):</span>
              <span className="text-sm font-medium text-blue-100">
                {estimatedReceiveAmount} {selectedToken}
              </span>
            </div>
            <p className="text-xs text-blue-400 mt-1">
              Estimated amounts - actual fees may vary slightly
            </p>
            <p className="text-xs text-blue-400 mt-1">
              Estimated time: 2-10 minutes for cross-chain transfer
            </p>
          </div>
        )}

        {/* Recipient Address */}
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-300">Recipient Address</label>
            {recipientSNS && (
              <span className="text-xs text-primary-400 font-medium">{recipientSNS}</span>
            )}
          </div>
          <Input
            type="text"
            value={recipient}
            onChange={(e) => handleRecipientChange(e.target.value)}
            placeholder="0x..."
            disabled={transactionState.isLoading}
            fullWidth
          />
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <svg className="w-3 h-3 text-amber-500/70" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-gray-500">Double-check the address — transactions cannot be reversed</span>
          </p>
        </div>


        {/* Transaction Status */}
        {transactionState.error && (
          <div className="bg-red-900/30 border border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-400">{transactionState.error}</p>
          </div>
        )}

        {transactionState.success && transactionState.hash && (
          <div className="bg-green-900/30 border border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-400">Transaction successful!</p>
            <a 
              href={`${CHAIN_CONFIGS[fromChain]?.explorerUrl}/tx/${transactionState.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary-400 hover:text-primary-300 underline mt-1 inline-block"
            >
              View on Explorer
            </a>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {needsApproval() && (
            <Button
              onClick={handleApproveToken}
              disabled={!isFormValid() || transactionState.isLoading || !isTokenAvailable()}
              isLoading={transactionState.isLoading}
              fullWidth
              variant="secondary"
            >
              Approve {selectedToken}
            </Button>
          )}
          
          <Button
            onClick={handleBridgeToken}
            disabled={!isFormValid() || transactionState.isLoading || needsApproval() || !isTokenAvailable()}
            isLoading={transactionState.isLoading}
            fullWidth
          >
            Bridge SHIDO
          </Button>
        </div>
      </div>
    </Card>
  );
};
